import { useMemo } from 'react'
import { GeoJsonFillLayer, GeoJsonLineLayer, MapMarker } from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { GROUND_BASE_WIDTH } from '../../lib/mapBorders'
import { groundFeatureCollection, groundWidthExpression, lineDashPattern, lineFeatureCollection, lineWidthExpression, segmentBearingDeg } from '../../lib/mapGeoJson'
import { formatLineLength, LINE_BORDER_FACTOR, lineLengthMeters, polylineMidpoint } from '../../lib/mapLines'
import type { GeoArea, GeoLine, GeoPt } from '../../lib/types'
import { COLORS } from '../../lib/theme'
import { DRAFT_LINE_CASING_ID, DRAFT_LINE_ID, DRAFT_POLYGON_FILL_ID, DRAFT_POLYGON_LINE_ID } from './editorLayerIds'
import type { LineDraft } from './mapLibreEditorTypes'

/** The dash a POLYGON draft is drawn with: fixed and proportional to width.
 *  MapLibre's `line-dasharray` units ARE line widths, so this reproduces the
 *  old canvas's `${2*w} ${1.5*w}` pattern exactly, without needing the
 *  resolved pixel width at all. It applies to the polygon draft only — a
 *  line/arrow draft is dashed according to the line's own STYLE, exactly like
 *  the committed line it is about to become (see the draft line layers below). */
const DRAFT_POLYGON_DASH = [2, 1.5]

function geoMidpoint(points: readonly GeoPt[]): GeoPt | null {
  const mid = polylineMidpoint(points.map((p) => [p.lon, p.lat]))
  return mid ? { lon: mid[0], lat: mid[1] } : null
}

export interface EditorDraftLayerProps {
  drawing: boolean
  drawPoints: GeoPt[]
  drawAccent: string
  drawShape: 'polygon' | 'line' | 'arrow'
  /** Wash the in-progress polygon. Off for a hunt border, which is saved and
   *  drawn as lines only — a wash previewed a filled area that vanished on
   *  save. Defaults to on, for the areas whose saved render is filled. */
  polygonFill?: boolean
  lineDraft?: LineDraft | null
  onFinishLineDraw?: () => void
  onCancelLineDraw?: () => void
}

/** The in-progress draw path: a dashed polygon/line/arrow preview, a live
 *  length tag for a line/arrow draft, and (once ≥2 points exist) the app's
 *  floating ✓/🗑 buttons — hosted on a `MapMarker` at the last draft point,
 *  the same anchor the old canvas floats them near. */
export function EditorDraftLayer({
  drawing,
  drawPoints,
  drawAccent,
  drawShape,
  polygonFill = true,
  lineDraft,
  onFinishLineDraw,
  onCancelLineDraw,
}: EditorDraftLayerProps) {
  const { t } = useTranslation('map')

  // `groundFeatureCollection` drops any ring under 3 points (real stored
  // areas never have fewer) — wrong for a LIVE draft, which passes through a
  // genuine 2-point stage before it has enough points to close a ring. That
  // stage is drawn separately, below, as a plain open two-point line.
  const polygonFC = useMemo(() => {
    if (drawShape !== 'polygon' || drawPoints.length < 3) return null
    const area: GeoArea = { id: 'draft', name: '', rings: [drawPoints], outerCount: 1 }
    return groundFeatureCollection([area], 'hunt', {})
  }, [drawShape, drawPoints])

  const twoPointFC = useMemo(() => {
    if (drawShape !== 'polygon' || drawPoints.length !== 2) return null
    return {
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: drawPoints.map((p) => [p.lon, p.lat]),
          },
        },
      ],
    }
  }, [drawShape, drawPoints])

  const draftLineFC = useMemo(() => {
    if (drawShape === 'polygon') return null
    const line: GeoLine = {
      id: 'draft',
      kind: drawShape,
      points: drawPoints,
      color: drawAccent,
      width: lineDraft?.width,
      style: lineDraft?.style,
    }
    return lineFeatureCollection([line], { lines: 1, arrows: 1 })
  }, [drawShape, drawPoints, drawAccent, lineDraft?.width, lineDraft?.style])

  if (!drawing || drawPoints.length === 0) return null

  const isPolygon = drawShape === 'polygon'
  const showLineDraft = !isPolygon && drawPoints.length >= 2 && draftLineFC
  const mid = showLineDraft ? geoMidpoint(drawPoints) : null
  const from = drawPoints.length >= 2 ? drawPoints[drawPoints.length - 2] : null
  const to = drawPoints[drawPoints.length - 1]
  const showFinishCancel = !isPolygon && drawPoints.length >= 2 && (onFinishLineDraw || onCancelLineDraw)

  // A line/arrow draft is dashed by the DRAFT's own style, not by the polygon
  // draft's fixed pattern — "same law as a committed line, so what is being
  // drawn matches what it becomes on release rather than changing on release"
  // is the rule the old SVG canvas stated and followed (`lineDashArray(
  // lineDraft?.style, lw)` on both of its draft strokes). Applying the polygon
  // dash here instead drew every line draft dashed, a SOLID one included, and
  // gave a genuinely dashed one the wrong runs.
  //
  // The casing's pattern is divided by LINE_BORDER_FACTOR for the same reason
  // the committed line's is (see `lineDashPattern`'s own doc comment): a
  // MapLibre dasharray unit is a multiple of the LAYER's OWN `line-width`, and
  // the casing draws 1.1x wider — so feeding both layers the same multiples
  // makes the black casing dashes 1.1x longer than the coloured ones and they
  // protrude past them at both ends.
  const draftCasingDash = lineDashPattern(lineDraft?.style, LINE_BORDER_FACTOR)
  const draftInteriorDash = lineDashPattern(lineDraft?.style)

  return (
    <>
      {isPolygon && polygonFC && (
        <>
          {polygonFill && (
            <GeoJsonFillLayer id={DRAFT_POLYGON_FILL_ID} data={polygonFC} paint={{ 'fill-color': drawAccent, 'fill-opacity': 0.2 }} />
          )}
          <GeoJsonLineLayer
            id={DRAFT_POLYGON_LINE_ID}
            data={polygonFC}
            paint={{
              'line-color': drawAccent,
              'line-width': groundWidthExpression(GROUND_BASE_WIDTH.hunt, 1),
              'line-dasharray': DRAFT_POLYGON_DASH,
            }}
          />
        </>
      )}
      {isPolygon && twoPointFC && (
        <GeoJsonLineLayer
          id={DRAFT_POLYGON_LINE_ID}
          data={twoPointFC}
          paint={{
            'line-color': drawAccent,
            'line-width': groundWidthExpression(GROUND_BASE_WIDTH.hunt, 1),
            'line-dasharray': DRAFT_POLYGON_DASH,
          }}
        />
      )}

      {showLineDraft && (
        <>
          <GeoJsonLineLayer
            id={DRAFT_LINE_CASING_ID}
            data={draftLineFC}
            paint={{
              'line-color': '#000',
              'line-width': lineWidthExpression(LINE_BORDER_FACTOR),
              ...(draftCasingDash ? { 'line-dasharray': draftCasingDash } : {}),
            }}
            layout={{ 'line-cap': 'butt', 'line-join': 'miter' }}
          />
          <GeoJsonLineLayer
            id={DRAFT_LINE_ID}
            data={draftLineFC}
            paint={{
              'line-color': drawAccent,
              'line-width': lineWidthExpression(),
              ...(draftInteriorDash ? { 'line-dasharray': draftInteriorDash } : {}),
            }}
            layout={{ 'line-cap': 'butt', 'line-join': 'miter' }}
          />
          {drawShape === 'arrow' && from && (
            <MapMarker lat={to.lat} lon={to.lon} anchor="center" rotation={segmentBearingDeg(from, to)}>
              <svg width={16} height={16} viewBox="0 0 16 16" style={{ display: 'block', overflow: 'visible' }}>
                <polygon points="8,0 0,16 16,16" fill={drawAccent} stroke="#000" strokeWidth={1} />
              </svg>
            </MapMarker>
          )}
          {mid && (
            <MapMarker lat={mid.lat} lon={mid.lon} anchor="center">
              <span
                data-testid="editor-draft-length"
                style={{
                  fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 12,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                  textShadow: '0 0 2.4px rgba(26,29,28,0.75), 0 0 2.4px rgba(26,29,28,0.75)',
                  pointerEvents: 'none',
                }}
              >
                {formatLineLength(lineLengthMeters(drawPoints))}
              </span>
            </MapMarker>
          )}
        </>
      )}

      {drawPoints.map((p, i) => (
        <MapMarker key={i} lat={p.lat} lon={p.lon} anchor="center">
          <svg width={8} height={8} viewBox="0 0 8 8" style={{ display: 'block', pointerEvents: 'none' }}>
            <circle cx={4} cy={4} r={4} fill="#fff" stroke={drawAccent} strokeWidth={1} />
            {i === 0 && <circle cx={4} cy={4} r={1.8} fill={drawAccent} />}
          </svg>
        </MapMarker>
      ))}

      {showFinishCancel && (
        <MapMarker lat={to.lat} lon={to.lon} anchor="bottom">
          <div style={{ display: 'flex', gap: 8, transform: 'translateY(-34px)' }}>
            {onFinishLineDraw && (
              <button
                type="button"
                data-testid="editor-draft-finish"
                title={t('toolActions.done')}
                onClick={(e) => {
                  e.stopPropagation()
                  onFinishLineDraw()
                }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  border: `1px solid ${COLORS.outlineVariant}`,
                  // Use a themed plate so glyph tokens (which invert on dark mode) stay readable
                  background: COLORS.surfaceContainerLowest,
                  color: COLORS.primary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ✓
              </button>
            )}
            {onCancelLineDraw && (
              <button
                type="button"
                data-testid="editor-draft-cancel"
                title={t('toolActions.cancel')}
                onClick={(e) => {
                  e.stopPropagation()
                  onCancelLineDraw()
                }}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  border: `1px solid ${COLORS.outlineVariant}`,
                  background: COLORS.surfaceContainerLowest,
                  color: COLORS.error,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                🗑
              </button>
            )}
          </div>
        </MapMarker>
      )}
    </>
  )
}
